import angular from 'angular'

import { makeAND } from '../utils/bits'
import { castArray } from 'lodash-es'

import StrictObj from '../utils/strict-obj'
import debugFields from '../utils/debug-fields'
import callTracker from '../utils/call-tracker'
import embedResize from '../utils/embed-resize'
import attachSetters from '../utils/attach-setters'
import EmbedPosition from '../utils/embed-position'
import postModelIndex from '../utils/post-model-index'
import MyIsotopeProxy from '../utils/my-isotope-proxy'
import viewportResize from '../utils/viewport-resize'
import attachOverriders from '../utils/attach-overriders'

import SumEmitter from '../utils/sum-emitter'
import ProxyEmitter from '../utils/proxy-emitter'

angular
  .module('ggs.gallery-preview-flow')
  .factory('FlowIsotope', FlowIsotopeProvider)

function FlowIsotopeProvider(
  $q,
  qh,
  env,
  $log,
  $interval,
  rootResize,
  fontEmitter,
  StayInQueue,
  ElementPosition,
  PreviewSizeManager) {

  const INTERVAL_DELAY = 0.7e3
  const INTERVAL_INF_LOOP = 0
  const INTERVAL_SKIP_APPLY = false
  const TRANSITION_DURATION = 0.4e3

  return function FlowIsotope(
    isoGridContentElm,
    isoGridShowcaseElm,
    carouselNextElm,
    carouselPrevElm,
    properties,
    canShowPreview,
    flowInViewportHandler) {

    const sign = 'galleryFlow::flowIsotope'
    const stayInQueue = StayInQueue(sign)

    const prependQueue = []
    const appendQueue = []

    const approvedPrependQueue = []
    const approvedAppendQueue = []

    const previewSizeManager = PreviewSizeManager(
      isoGridContentElm,
      isoGridShowcaseElm,
      properties)

    const resizeEmitter =
      SumEmitter(
        fontEmitter,
        rootResize.width,
        ProxyEmitter(viewportResize.height, properties.key2get.carousel_mode),
        ProxyEmitter(embedResize.viewportHeight, properties.key2get.carousel_mode))

    const intervalPrependId = $interval(
      intervalPrepend,
      INTERVAL_DELAY,
      INTERVAL_INF_LOOP,
      INTERVAL_SKIP_APPLY)

    const intervalAppendId = $interval(
      intervalAppend,
      INTERVAL_DELAY,
      INTERVAL_INF_LOOP,
      INTERVAL_SKIP_APPLY)

    resizeEmitter.on(resize)

    // in-viewport queries
    const bodyQuery = (local) => false
      || local.fitViewport && local.height !== 0
      || local.calcFillViewportPart(0.7)

    const headQuery = (local) => false
      || local.fitViewport
      || local.topInViewport && local.absTopOffset < local.viewportHeight * 3 / 4

    const tailQuery = (local) => false
      || local.fitViewport
      || local.bottomInViewport
      || local.bottomBelowViewport && local.absBottomOffset < local.viewportHeight * 4
      || local.bottomAboveViewport && local.absBottomOffset < local.viewportHeight * 2

    const lastQuery = (local) => false
      || local.fitViewport
      || local.bottomInViewport
      || local.bottomBelowViewport && local.absBottomOffset < local.viewportHeight * 3 / 4
      || local.bottomAboveViewport && local.absBottomOffset < local.viewportHeight * 2

    // in-viewport store
    const isInViewport = attachOverriders(attachSetters(StrictObj({
      head: true,
      tail: true
    })))

    // in-viewport handlers
    const embedInViewportHandler = StrictObj({
      body: qh.makeIf(
        env.getters.isIframeMode,
        flowInViewportHandler),
      head: qh.makeIf(
        makeAND(
          env.getters.isIframeMode,
          properties.key2get.negate.carousel_mode),
        isInViewport.setters.head),
      tail: qh.makeIf(
        makeAND(
          env.getters.isIframeMode,
          properties.key2get.negate.carousel_mode),
        isInViewport.setters.tail),
      last: qh.makeIf(
        makeAND(
          env.getters.isIframeMode,
          properties.key2get.negate.carousel_mode),
        switchAnimateMode)
    })

    const masonryInViewportHandler = StrictObj({
      body: qh.makeIf(
        env.getters.negate.isIframeMode,
        flowInViewportHandler),
      head: qh.makeIf(
        makeAND(
          env.getters.negate.isIframeMode,
          properties.key2get.negate.carousel_mode),
        isInViewport.setters.head),
      tail: qh.makeIf(
        makeAND(
          env.getters.negate.isIframeMode,
          properties.key2get.negate.carousel_mode),
        isInViewport.setters.tail),
      last: qh.makeIf(
        makeAND(
          env.getters.negate.isIframeMode,
          properties.key2get.negate.carousel_mode),
        switchAnimateMode)
    })

    const carouselInViewportHandler = StrictObj({
      head: qh.makeIf(
        properties.key2get.carousel_mode,
        isInViewport.setters.head),
      tail: qh.makeIf(
        properties.key2get.carousel_mode,
        isInViewport.setters.tail)
    })

    // embed in-viewport detectors
    const embedBodyPosition = EmbedPosition(bodyQuery, embedInViewportHandler.body)
    const embedHeadPosition = EmbedPosition(headQuery, embedInViewportHandler.head)
    const embedTailPosition = EmbedPosition(tailQuery, embedInViewportHandler.tail)
    const embedLastPosition = EmbedPosition(lastQuery, embedInViewportHandler.last)

    // masonry in-viewport detectors
    const masonryBodyPosition = ElementPosition(isoGridShowcaseElm, bodyQuery, masonryInViewportHandler.body)
    const masonryHeadPosition = ElementPosition(isoGridShowcaseElm, headQuery, masonryInViewportHandler.head)
    const masonryTailPosition = ElementPosition(isoGridShowcaseElm, tailQuery, masonryInViewportHandler.tail)
    const masonryLastPosition = ElementPosition(isoGridShowcaseElm, lastQuery, masonryInViewportHandler.last)

    // carousel in-viewport detectors + carousel definition
    const myIsotopeProxy = MyIsotopeProxy(
      TRANSITION_DURATION,
      properties.key2get.carousel_mode,
      isoGridContentElm,
      isoGridShowcaseElm,
      carouselNextElm,
      carouselPrevElm,
      descDiffPairs,
      properties.key2get.highlight_period,
      previewSizeManager.castToX1,
      previewSizeManager.castToX2,
      previewSizeManager.getMasonrySizerWidth,
      previewSizeManager.getCarouselSizerHeight,
      previewSizeManager.getMasonryWidth,
      previewSizeManager.getCarouselHeight,
      previewSizeManager.getCarouselWidth,
      carouselInViewportHandler.head,
      carouselInViewportHandler.tail)

    // pure
    const isoInit = callTracker(myIsotopeProxy.init)
    const isoActive = callTracker(myIsotopeProxy.active)

    // npe
    const isoDestroy = callTracker(isoNPE(myIsotopeProxy.destroy))
    const isoContains = callTracker(isoNPE(myIsotopeProxy.contains))
    const isoSilentMode = callTracker(isoNPE(myIsotopeProxy.silentMode))
    const isoAnimateMode = callTracker(isoNPE(myIsotopeProxy.animateMode))

    // queue + npe
    const isoResize = callTracker(
      stayInQueue.make(
        isoNPE(myIsotopeProxy.resize)))

    const isoScrollTo = callTracker(
      stayInQueue.make(
        isoNPE(myIsotopeProxy.scrollTo)))

    const isoSwitchType = callTracker(
      stayInQueue.make(
        isoNPE(myIsotopeProxy.switchType)))

    // queue + handler management + npe
    const isoRemove = callTracker(
      stayInQueue.make(
          isoNPE(myIsotopeProxy.remove)))

    const isoRemoveSilent = callTracker(
      stayInQueue.make(
        isoNPE(myIsotopeProxy.removeSilent)))

    const isoRemoveVirtual = callTracker(
      stayInQueue.make(
        isoNPE(myIsotopeProxy.removeVirtual)))

    // queue + delay + npe
    const isoArrange = callTracker(
      stayInQueue.make(
        isoMakeDelay(
          isoNPE(myIsotopeProxy.arrange),
          TRANSITION_DURATION)))

    const isoPrepend = callTracker(
      stayInQueue.make(
        isoMakeDelay(
          isoNPE(myIsotopeProxy.prepend),
          TRANSITION_DURATION)))

    const isoAppend = callTracker(
      stayInQueue.make(
        isoMakeDelay(
          isoNPE(myIsotopeProxy.append),
          TRANSITION_DURATION)))

    // export
    return StrictObj(debugFields({
      flushPrependQueue,
      flushAppendQueue,

      lazyMode,
      instantMode,

      destroy,

      scrollTo,
      arrange,
      resize,
      removeVirtual,
      remove,
      prepend,
      append,

      debugMyIsotopeProxy: () => myIsotopeProxy,
      debugStayInQueue: () => stayInQueue
    }, {
      _prependQueue: prependQueue,
      _appendQueue: appendQueue,
      _approvedPrependQueue: approvedPrependQueue,
      _approvedAppendQueue: approvedAppendQueue,
      _isInViewport: isInViewport,
      _resizeEmitter: resizeEmitter,
      _previewSizeManager: previewSizeManager
    }))

    // force process queues
    function flushPrependQueue() {
      if (prependQueue.length !== 0 && stayInQueue.idle()) {
        return realPrepend()
      }
    }

    function flushAppendQueue() {
      if (appendQueue.length !== 0 && stayInQueue.idle()) {
        return realAppend()
      }
    }

    // lazy/instant mode
    function lazyMode() {
      $log.debug(`${sign}: instantMode is off!`)
      isInViewport.overriders.reset.head()
      isInViewport.overriders.reset.tail()
    }

    function instantMode() {
      $log.debug(`${sign}: instantMode is on!`)
      isInViewport.overriders.head(true)
      isInViewport.overriders.tail(true)
    }

    // switch animate mode

    function switchAnimateMode(animateMode) {
      return animateMode
        ? isoAnimateMode()
        : isoSilentMode()
    }

    // destroy

    function destroy() {
      $interval.cancel(intervalPrependId)
      $interval.cancel(intervalAppendId)
      isoDestroy()
      resizeEmitter.off(resize)

      embedBodyPosition.destroy()
      embedHeadPosition.destroy()
      embedTailPosition.destroy()
      embedLastPosition.destroy()

      masonryBodyPosition.destroy()
      masonryHeadPosition.destroy()
      masonryTailPosition.destroy()
      masonryLastPosition.destroy()
    }

   // scrollTo

    function scrollTo(post) {
      return isoScrollTo(post)
    }

    // arrange

    function arrange() {
      previewSizeManager.dropCache()
      isoSwitchType()
      return isoArrange()
    }

    // resize

    function resize() {
      previewSizeManager.dropCache()
      isoSwitchType()
      return isoResize()
    }

    // removeVirtual

    function removeVirtual(input) {
      const posts = castArray(input)
      removeFromQueue(posts, prependQueue)
      removeFromQueue(posts, appendQueue)
      removeFromQueue(posts, approvedPrependQueue)
      removeFromQueue(posts, approvedAppendQueue)
      return removeVirtualFromIsotope(posts)
    }

    function removeVirtualFromIsotope(posts) {
      return isoRemoveVirtual(posts)
    }

    // remove

    function remove(input) {
      const posts = castArray(input)
      removeFromQueue(posts, prependQueue)
      removeFromQueue(posts, appendQueue)
      removeFromQueue(posts, approvedPrependQueue)
      removeFromQueue(posts, approvedAppendQueue)
      return removeFromIsotope(posts)
    }

    function removeFromIsotope(posts) {
      const postsToRemove = []
      for (const post of posts) {
        if (isoContains(post)) {
          postsToRemove.push(post)
        }
      }

      if (!postsToRemove.length === 0) {
        return
      }

      const maxChunkSize = 32
      if (postsToRemove.length <= maxChunkSize) {
        return isoRemove(postsToRemove)
      }

      const topChunk = postsToRemove.splice(0, maxChunkSize)
      return qh.join(
        isoRemoveSilent(postsToRemove),
        isoRemove(topChunk))
    }

    // prepend, intervalPrepend, realPrepend

    function prepend(reg) {
      prependQueue.unshift(reg)
    }

    function intervalPrepend() {
      if (prependQueue.length !== 0 && isInViewport.head && stayInQueue.idle()) {
        return realPrepend()
      }
    }

    function realPrepend() {
      const localQueue = prependQueue.concat().sort(descDiffPairs)
      prependQueue.length = 0

      for (let index = localQueue.length - 1; index !== -1; index--) {
        const reg = localQueue[index]
        const canShow = canShowPreview(reg.post)

        if (canShow) {
          approvedPrependQueue.unshift(reg)
          reg.reportPreviewDone()
        } else {
          reg.reportPreviewDeny()
        }
      }

      const localApprovedQueue = approvedPrependQueue.concat()
      const localApprovedPairs = []
      approvedPrependQueue.length = 0

      for (let index = localApprovedQueue.length - 1; index !== -1; index--) {
        const reg = localApprovedQueue[index]
        const pair = StrictObj({
          elm: reg.elm,
          post: reg.post,
          applyShowcaseSize: reg.applyShowcaseSize,
          calcContentWidth: reg.calcContentWidth,
          calcContentHeight: reg.calcContentHeight
        })
        reg.prepareToDisplay()
        localApprovedPairs.unshift(pair)
      }

      return qh.join(
        isoInit(),
        isoPrepend(localApprovedPairs))
    }

    // append, intervalAppend, realAppend

    function append(reg) {
      appendQueue.push(reg)
    }

    function intervalAppend() {
      if (appendQueue.length !== 0 && isInViewport.tail && stayInQueue.idle()) {
        return realAppend()
      }
    }

    function realAppend() {
      const localQueue = appendQueue.concat().sort(descDiffPairs)
      appendQueue.length = 0

      for (const reg of localQueue) {
        const canShow = canShowPreview(reg.post)

        if (canShow) {
          approvedAppendQueue.push(reg)
          reg.reportPreviewDone()
        } else {
          reg.reportPreviewDeny()
        }
      }

      const localApprovedQueue = approvedAppendQueue.concat()
      const localApprovedPairs = []
      approvedAppendQueue.length = 0

      for (const reg of localApprovedQueue) {
        const pair = StrictObj({
          elm: reg.elm,
          post: reg.post,
          applyShowcaseSize: reg.applyShowcaseSize,
          calcContentWidth: reg.calcContentWidth,
          calcContentHeight: reg.calcContentHeight
        })
        reg.prepareToDisplay()
        localApprovedPairs.push(pair)
      }

      return qh.join(
        isoInit(),
        isoAppend(localApprovedPairs))
    }

    // helpers

    function descDiffPairs(a, b) {
      return postModelIndex.diffDesc(a.post, b.post)
    }

    function removeFromQueue(posts, queue) {
      for (let index = queue.length - 1; index !== -1; index--) {
        if (posts.indexOf(queue[index].post) !== -1) {
          queue.splice(index, 1)
        }
      }
    }

    function isoNPE(action) {
      return debugFields(isoNPE_wrapper, {
        _action: action
      })

      function isoNPE_wrapper() {
        if (isoActive()) {
          return action.apply(null, arguments)
        } else {
          return false
        }
      }
    }

    function isoMakeDelay(action, delay) {
      return debugFields(isoMakeDelay_wrapper, {
        _delay: delay,
        _action: action
      })

      function isoMakeDelay_wrapper() {
        try {
          const res = action.apply(null, arguments)
          return qh.delay(delay, res)
        } catch (err) {
          return $q.reject(err)
        }
      }
    }
  }
}
